import { getStoredRemoteConfig, wsUrlFor } from "./remoteConfig";

export const TOKEN_KEY = "blackred-studio.session.v1";
export const ACTIVE_USER_KEY = "blackred-studio.active-user.v1";
export const ACTIVE_USER_EVENT = "blackred-studio:active-user";

export interface ActiveUser {
  id: string;
  email: string;
  name: string;
  phone: string;
  company: string;
  country: string;
  jobTitle: string;
  profileComplete: boolean;
  role: "admin" | "editor" | "viewer";
  active: boolean;
  createdAt: string;
}

export function cacheActiveUser(user: Partial<ActiveUser> & Pick<ActiveUser, "email" | "name">) {
  const normalized: ActiveUser = {
    id: user.id || "",
    email: user.email,
    name: user.name,
    phone: user.phone || "",
    company: user.company || "",
    country: user.country || "",
    jobTitle: user.jobTitle || "",
    profileComplete: Boolean(user.profileComplete || user.phone),
    role: user.role || "admin",
    active: user.active ?? true,
    createdAt: user.createdAt || "",
  };
  sessionStorage.setItem(ACTIVE_USER_KEY, JSON.stringify(normalized));
  window.dispatchEvent(new CustomEvent<ActiveUser>(ACTIVE_USER_EVENT, { detail: normalized }));
  return normalized;
}

export function getCachedActiveUser(): ActiveUser | null {
  try {
    const value = sessionStorage.getItem(ACTIVE_USER_KEY);
    return value ? JSON.parse(value) as ActiveUser : null;
  } catch {
    return null;
  }
}

/** Reproduce en el frontend el mismo slug que el backend usa para nombrar la tabla real
 *  (ver `sanitizeIdentifier` en electron/backend.cjs), para poder anticipar el nombre "ent_*"
 *  antes de publicar y para resolver relaciones entre tablas del proyecto. */
export function entityNameFor(tableName: string): string {
  const slug = String(tableName || "").trim().toLowerCase().replace(/[^a-z0-9_]+/g, "_").replace(/^_+/, "");
  return `ent_${slug}`;
}

export interface BackendContext {
  baseUrl: string;
  wsUrl: string;
  accessKey: string;
  token: string;
}

/** Resuelve la cuenta cloud activa. Todas las plataformas usan exactamente la misma ruta
 *  HTTPS/WSS; Electron no dispone de un backend ni de una base de datos local. */
export async function getBackendContext(): Promise<BackendContext> {
  const remote = getStoredRemoteConfig();
  return {
    baseUrl: remote.baseUrl,
    wsUrl: remote.baseUrl ? wsUrlFor(remote.baseUrl) : "",
    accessKey: remote.accessKey,
    token: sessionStorage.getItem(TOKEN_KEY) || "",
  };
}

export function requireSession(ctx: BackendContext) {
  if (!ctx.baseUrl || !ctx.token) throw new Error("Inicia sesión en BlackRed Cloud para usar esta función.");
}

export async function apiRequest<T = unknown>(ctx: BackendContext, path: string, options: RequestInit = {}): Promise<T> {
  if (!ctx.baseUrl) throw new Error("No hay una cuenta de BlackRed Cloud conectada.");
  const response = await fetch(`${ctx.baseUrl}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(ctx.accessKey ? { "X-BlackRed-Key": ctx.accessKey } : {}),
      ...(ctx.token ? { Authorization: `Bearer ${ctx.token}` } : {}),
      ...(options.headers || {}),
    },
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(payload.error || `Error ${response.status}`);
  return payload as T;
}

/** Construye la URL con `?key=` para recursos que deben poder cargarse desde HTML plano
 *  (`<img src>`, `<a href>`), donde el navegador no puede enviar el header X-BlackRed-Key. */
export function keyedAssetUrl(ctx: BackendContext, path: string): string {
  if (!ctx.accessKey) return `${ctx.baseUrl}${path}`;
  const separator = path.includes("?") ? "&" : "?";
  return `${ctx.baseUrl}${path}${separator}key=${encodeURIComponent(ctx.accessKey)}`;
}

export interface UploadedFile {
  id: string;
  filename: string;
  mimeType: string;
  size: number;
  url: string;
}

/** Sube un archivo (ya leído como data URL / base64) a /api/uploads y devuelve su descriptor.
 *  El valor que se guarda en un campo tipo "file" de una entidad es este descriptor serializado. */
export async function uploadFile(ctx: BackendContext, file: File): Promise<UploadedFile> {
  const dataBase64 = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(reader.error || new Error("No se pudo leer el archivo"));
    reader.readAsDataURL(file);
  });
  return apiRequest<UploadedFile>(ctx, "/api/uploads", {
    method: "POST",
    body: JSON.stringify({ filename: file.name, mimeType: file.type || "application/octet-stream", dataBase64 }),
  });
}

export interface RealtimeMessage {
  type: "entity" | "project" | string;
  entity?: string;
  op?: "create" | "update" | "delete" | "adjust" | string;
  id?: unknown;
  [key: string]: unknown;
}

/** Se conecta al WebSocket del backend cloud y llama a `onMessage` con cada evento en tiempo
 *  real (cambios en entidades, autoguardado del proyecto). Reintenta la conexión con backoff
 *  si se cae. Devuelve una función para cerrar la conexión (llamar en el cleanup del efecto). */
export function connectRealtime(ctx: BackendContext, onMessage: (message: RealtimeMessage) => void): () => void {
  if (!ctx.wsUrl || typeof WebSocket === "undefined") return () => {};
  const url = ctx.accessKey ? `${ctx.wsUrl}?key=${encodeURIComponent(ctx.accessKey)}` : ctx.wsUrl;
  let socket: WebSocket | null = null;
  let closed = false;
  let retryDelay = 1000;
  let retryTimer: ReturnType<typeof setTimeout> | undefined;

  function connect() {
    if (closed) return;
    try {
      socket = new WebSocket(url);
    } catch {
      return;
    }
    socket.onmessage = (event) => {
      try {
        onMessage(JSON.parse(String(event.data)));
      } catch {
        /* mensaje no válido, se ignora */
      }
    };
    socket.onopen = () => { retryDelay = 1000; };
    socket.onclose = () => {
      if (closed) return;
      retryTimer = setTimeout(connect, retryDelay);
      retryDelay = Math.min(retryDelay * 1.5, 15000);
    };
  }

  connect();
  return () => {
    closed = true;
    if (retryTimer) clearTimeout(retryTimer);
    socket?.close();
  };
}
